import React, { useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { useAdminTx } from "../../hooks/useContract";
import { useNotification } from "../../hooks/useNotification";
import { shortAddress, formatUSDT, parseUSDT } from "../../utils/format";
import Button from "../../components/Common/Button";
import { Card } from "../../components/Common/Card";
import { CONTRACT_ADDRESS } from "../../config/contract";

function AdminWallets() {
  const notify = useNotification();
  const { getContract, getUsdt } = useWeb3();
  const { send, pending } = useAdminTx();

  const [addrs, setAddrs] = useState({ team: "", missing: "" });
  const [contractBal, setContractBal] = useState(ethers.BigNumber.from(0));
  const [newTeam, setNewTeam] = useState("");
  const [newMissing, setNewMissing] = useState("");
  const [withdrawAmt, setWithdrawAmt] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    const load = async () => {
      const contract = getContract();
      const usdt = getUsdt();
      if (!contract) return;
      try {
        const [team, missing] = await Promise.all([
          contract.teamWallet(),
          contract.missingIncomeWallet(),
        ]);
        let bal = ethers.BigNumber.from(0);
        if (usdt) {
          try { bal = await usdt.balanceOf(CONTRACT_ADDRESS); } catch {}
        }
        if (!cancelRef.current) {
          setAddrs({ team, missing });
          setContractBal(bal);
        }
      } catch {
        // ignore
      }
    };
    load();
    return () => {
      cancelRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const updateTeam = async () => {
    if (!ethers.utils.isAddress(newTeam)) return notify.error("Invalid wallet");
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.updateTeamWallet(newTeam),
      {
        pendingMsg: "Updating team wallet...",
        successMsg: "Team wallet updated",
        action: "update_team_wallet",
        params: { newWallet: newTeam },
      }
    );
    if (ok) {
      setNewTeam("");
      reload();
    }
  };

  const updateMissing = async () => {
    if (!ethers.utils.isAddress(newMissing)) return notify.error("Invalid wallet");
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.updateMissingIncomeWallet(newMissing),
      {
        pendingMsg: "Updating wallet...",
        successMsg: "Missing income wallet updated",
        action: "update_missing_wallet",
        params: { newWallet: newMissing },
      }
    );
    if (ok) {
      setNewMissing("");
      reload();
    }
  };

  const doEmergencyWithdraw = async () => {
    if (!withdrawAmt || Number(withdrawAmt) <= 0) return notify.error("Enter an amount");
    if (!window.confirm(`Withdraw ${withdrawAmt} USDT to your wallet?`)) return;
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.emergencyWithdraw(parseUSDT(withdrawAmt)),
      {
        pendingMsg: "Withdrawing...",
        successMsg: "Withdrawn",
        action: "emergency_withdraw",
        params: { amount: String(withdrawAmt) },
      }
    );
    if (ok) {
      setWithdrawAmt("");
      reload();
    }
  };

  return (
    <div>
      {/* Team Wallet */}
      <div className="section-header">
        <div className="section-title">Team Wallet</div>
      </div>
      <Card className="mb-lg">
        <div className="stat-card-label">Current</div>
        <div className="mono text-cyan mb-md" style={{ fontSize: 13, wordBreak: "break-all" }}>
          {addrs.team || "—"}
        </div>
        <div className="input-group">
          <label className="input-label">New Team Wallet</label>
          <input
            className="input"
            placeholder="0x..."
            value={newTeam}
            onChange={(e) => setNewTeam(e.target.value)}
          />
        </div>
        <Button variant="primary" full loading={pending} onClick={updateTeam}>
          Update Team Wallet
        </Button>
      </Card>

      {/* Missing Income Wallet */}
      <div className="section-header">
        <div className="section-title">Missing Income Wallet</div>
      </div>
      <Card className="mb-lg">
        <div className="stat-card-label">Current</div>
        <div className="mono text-cyan mb-md" style={{ fontSize: 13, wordBreak: "break-all" }}>
          {addrs.missing || "—"}
        </div>
        <div className="input-group">
          <label className="input-label">New Missing Income Wallet</label>
          <input
            className="input"
            placeholder="0x..."
            value={newMissing}
            onChange={(e) => setNewMissing(e.target.value)}
          />
        </div>
        <Button variant="primary" full loading={pending} onClick={updateMissing}>
          Update Missing Income Wallet
        </Button>
      </Card>

      {/* Emergency Withdraw */}
      <div className="section-header">
        <div className="section-title">Emergency Withdraw</div>
      </div>
      <Card className="mb-lg">
        <div className="stat-card-label">Contract USDT Balance</div>
        <div style={{
          fontFamily: "'Sora', 'Inter', sans-serif",
          fontSize: 26,
          fontWeight: 700,
          color: "var(--success)",
          letterSpacing: "-0.02em",
          marginBottom: 12,
        }}>
          ${formatUSDT(contractBal)}
        </div>
        <div className="network-warning" style={{ marginBottom: 12 }}>
          ⚠ Withdraws USDT from contract directly to your connected wallet.
        </div>
        <div className="input-group">
          <label className="input-label">Amount (USDT)</label>
          <input
            type="number"
            className="input"
            value={withdrawAmt}
            onChange={(e) => setWithdrawAmt(e.target.value)}
            placeholder="0.00"
          />
        </div>
        <Button variant="ghost" full loading={pending} onClick={doEmergencyWithdraw}>
          🚨 Withdraw
        </Button>
      </Card>
    </div>
  );
}

export default AdminWallets;
